"use client";

import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { toast } from "react-hot-toast";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Breadcrumbs from "@/components/Breadcrumbs";
import AdSection from "@/components/AdSection";
import CountrySelectorGenerator from "@/components/NumberGenerator/CountrySelectorGenerator";
import ServiceSelectorGenerator from "@/components/NumberGenerator/ServiceSelectorGenerator";
import MessagesReceived from "@/components/ClientComponents/MessagesReceived";
import { userAtom } from "@/lib/atoms/user";
import { Hyphenated } from "@/utils/Hyphenated";
import { SERVICES } from "@/data/services-data";

export default function SmsVerificationClient({ countries }) {
  const [LocalUser] = useAtom<any>(userAtom);
  const router = useRouter();

  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [order, setOrder] = useState<{ orderId: number; number: string } | null>(
    null
  );

  // Resume from the URL: ?service= pre-fills the picker (e.g. arriving from
  // a /sms-verification/[service] landing page); ?orderId= resumes the
  // waiting-for-code panel after a refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get("service");
    const orderIdParam = params.get("orderId");
    const numberParam = params.get("number");

    if (serviceParam) {
      const match = SERVICES.find((s) => s.value === serviceParam);
      if (match) setSelectedService(match);
    }
    if (orderIdParam && numberParam) {
      setOrder({ orderId: Number(orderIdParam), number: numberParam });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const price = selectedService ? selectedService.priceCents / 100 : 0;

  const buildTopUpHref = () => {
    const params = new URLSearchParams();
    params.set("ref", "sms-verification");
    if (selectedService) params.set("service", selectedService.value);
    if (price) params.set("price", String(price));
    return `/account/add-credit?${params.toString()}`;
  };

  const handlePay = async () => {
    if (!selectedService || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/sms-verification/order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serviceSlug: selectedService.value }),
      });
      const data = await res.json();
      if (res.ok) {
        setOrder({ orderId: data.orderId, number: data.number });
        window.history.pushState(
          {},
          "",
          `/sms-verification?service=${selectedService.value}&orderId=${data.orderId}&number=${data.number}`
        );
        toast.success("Number allocated — waiting for your code.");
      } else if (res.status === 401) {
        router.push("/auth/login");
      } else if (res.status === 402) {
        toast.error("Insufficient balance — please top up first.");
        router.push(buildTopUpHref());
      } else {
        toast.error(data.error || "Failed to create order");
      }
    } catch {
      toast.error("Something went wrong, please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartOver = () => {
    setOrder(null);
    setSelectedCountry(null);
    setSelectedService(null);
    window.history.pushState({}, "", "/sms-verification");
  };

  const copyToClipboard = (number: string) => {
    navigator.clipboard.writeText(number);
    toast.success("Number Copied");
  };

  if (order) {
    return (
      <div className="py-4 lg:py-14 space-y-6">
        <Breadcrumbs capitalizeLinks />

        <div className="w-full text-center space-y-4">
          <h1 className="font-bold text-2xl">
            {selectedService?.name} SMS Verification Number
          </h1>
          <div className="flex items-center justify-center space-x-1">
            <h2 className="font-spaceGrotesk font-bold text-3xl">
              +{order.number}
            </h2>
            <div
              onClick={() => copyToClipboard(`+${order.number}`)}
              className="p-2 hover:bg-gray-100 cursor-pointer rounded-full"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6 md:w-8 md:h-8"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
                />
              </svg>
            </div>
          </div>
          <p className="text-gray-600 font-medium max-w-xl mx-auto">
            Enter this number where {selectedService?.name} asks for a phone
            number. Your one-time code will appear below automatically —
            it&apos;s good for this one verification only.
          </p>
          <button
            onClick={handleStartOver}
            className="text-sm underline text-gray-500 hover:text-gray-700"
          >
            Start another verification
          </button>
        </div>

        <div className="py-8">
          <MessagesReceived
            endpoint={`/api/messages/verification?orderId=${order.orderId}`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="py-4 lg:py-14 space-y-6">
      <Breadcrumbs capitalizeLinks />

      <div className="bg-gradient-to-tr to-[#090EDB] from-[#E73DC4] rounded-2xl py-12 md:py-16 lg:py-20 px-4 md:px-8">
        <div className="max-w-2xl mx-auto w-full">
          <div className="text-center mb-8 md:mb-10 space-y-3">
            <h1 className="font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight">
              SMS Verification
            </h1>
            <h2 className="font-bold text-xl md:text-2xl lg:text-3xl text-white">
              Get a One-Time Verification Code, Guaranteed
            </h2>
            <p className="text-gray-100 text-sm md:text-base font-medium max-w-lg mx-auto">
              Choose a country and a service, pay a small per-code fee, and
              we&apos;ll allocate a number for you right away. Your
              verification code shows up here automatically the moment it
              arrives.
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-full max-w-md mx-auto">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Select Country
              </label>
              <CountrySelectorGenerator
                data={countries}
                value={selectedCountry}
                handleMenuItemClick={setSelectedCountry}
              />
            </div>

            <div className="w-full max-w-md mx-auto">
              <label className="block text-white/90 text-sm font-semibold mb-2">
                Select Service
              </label>
              <ServiceSelectorGenerator
                value={selectedService?.label}
                handleMenuItemClick={setSelectedService}
              />
            </div>
          </div>

          {selectedCountry && selectedService && (
            <div className="mt-8 bg-white rounded-xl p-6 space-y-4 max-w-md mx-auto">
              <p className="text-gray-700 font-medium text-center">
                Allocating a number that works with{" "}
                <span className="font-bold">{selectedService.name}</span> in{" "}
                <span className="font-bold">
                  {selectedCountry.country_flag} {selectedCountry.country_name}
                </span>
              </p>
              <div className="flex items-center justify-between border-t pt-4">
                <span className="text-gray-500">Price per code:</span>
                <span className="font-bold text-lg">${price.toFixed(2)}</span>
              </div>
              {typeof LocalUser?.balance === "number" && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Your balance:</span>
                  <span>${LocalUser.balance}</span>
                </div>
              )}
              <button
                onClick={handlePay}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:from-[#090EDB]/90 hover:to-[#E73DC4]/90 disabled:opacity-60 text-white font-bold text-lg py-4 px-6 rounded-xl transition-all duration-200 shadow-lg"
              >
                {isSubmitting ? "Processing…" : `Pay $${price.toFixed(2)} & Get Number`}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto space-y-4">
        <h2 className="font-semibold text-2xl">Browse SMS Verification by Service</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {SERVICES.map((service) => (
            <Link
              key={service.value}
              href={`/sms-verification/${service.value}`}
              className="border rounded-lg px-4 py-3 text-center font-medium text-gray-700 hover:border-primary hover:text-primary transition-colors"
            >
              {service.name}
            </Link>
          ))}
        </div>
      </div>

      <div className="text-sm space-y-4 p-8 text-gray-700 border-[#090EDB] border-[2px] max-w-xl mx-auto rounded-xl">
        <h3 className="font-bold text-lg">Want a number only you can use?</h3>
        <p>
          This number can be used by other people too — your code is matched
          to your specific request, so you&apos;ll only ever see your own
          code. For guaranteed privacy and unlimited verifications on a
          number only you control, rent your own dedicated private number.
        </p>
        <div className="flex items-center justify-center">
          <Link href="/rent-private-number">
            <div className="bg-gradient-to-tr from-[#090EDB] to-[#E73DC4] hover:bg-gradient-to-tr hover:from-[#090EDB]/80 hover:to-[#E73DC4]/80 px-10 transition-all duration-300 hover:px-14 py-3 text-white font-semibold rounded-lg">
              Rent VIP Private Number
            </div>
          </Link>
        </div>
      </div>

      <AdSection />
    </div>
  );
}
